'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';

import { earnSummaryBunja, getEarnSummaryOfCurrency } from '@flipster/features/src/shared/earn/state';
import { Tooltip } from '@flipster/react/design-system-legacy/tooltip';
import { IconButton } from '@flipster/react/design-system/icon-button';
import Formatted from '@flipster/react/design-utils/formatted';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import InformationCircleIcon from '@flipster/react/icon/generated/InformationCircleIcon';
import { css } from '@flipster/styled-system/css';
import { hstack, square, stack } from '@flipster/styled-system/patterns';
import { alert } from '@flipster/ui/Alert';
import {
  NonUsdCurrencyList,
  NonUsdPrecision,
  Usd1Currency,
  UsdPrecision,
  UsdtCurrency,
  UsdtCurrencyList,
} from '@flipster/utils/constants/currency';
import { BigNumber } from '@flipster/utils/num';

export const AprAppliedBalance = ({ currency }: { currency: string }) => {
  const { earnSummaryAtom } = useBunja(earnSummaryBunja);
  const earnSummary = useAtomValue(earnSummaryAtom);

  const { isBalanceEmpty, allocations, noAprAppliedBalance } = getEarnSummaryOfCurrency(currency, earnSummary);

  const getTooltipContents = () => {
    if ([...NonUsdCurrencyList, Usd1Currency].includes(currency)) {
      return (
        <ul className={css({ list: 'disc' })}>
          <li>Balance used for daily earnings calculation.</li>
          <li>Includes unrealized P&L, updated on every entrance to earn page.</li>
          <li>Calculated based on earn limit for each APR category.</li>
        </ul>
      );
    }
    return (
      <ul className={css({ list: 'disc' })}>
        <li>Balance used for daily earnings calculation.</li>
        <li>Includes unrealized P&L, updated on every entrance to earn page.</li>
        <li>Unrealized P&L is applied first to USDT, then to Premium Earn, then to USDE.</li>
        <li>Calculated based on earn limit for each APR category.</li>
      </ul>
    );
  };

  return (
    <details className={css({ borderTop: '1px solid', borderColor: 'border.default.level-1' })}>
      <summary
        className={hstack({
          h: 48,
          cursor: isBalanceEmpty ? 'default' : 'pointer',
          userSelect: 'none',
          justify: 'space-between',
        })}
      >
        <div className={hstack()}>
          <span
            className={css({
              textStyle: 'Text/M/Regular',
              color: 'content.default.level-4',
            })}
          >
            APR applied balance
          </span>
          <Tooltip
            placement='bottom'
            crossAxisOffset={75}
            maxWidth={500}
            mobileAlert={() => {
              alert.show({
                title: 'APR applied balance',
                message: getTooltipContents(),
                confirmText: 'OK',
              });
            }}
          >
            <Tooltip.Trigger asChild>
              <IconButton icon={<InformationCircleIcon />} size='small' hierarchy='quaternary' />
            </Tooltip.Trigger>
            <Tooltip.Content>{getTooltipContents()}</Tooltip.Content>
          </Tooltip>
        </div>
        {isBalanceEmpty ? (
          <span className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-2' })}>None</span>
        ) : (
          <span
            className={square({
              size: 24,
              color: 'content.default.level-3',
              '[open] &': { transform: 'rotate(-180deg)' },
            })}
          >
            <ChevronDownIcon size={16} />
          </span>
        )}
      </summary>
      {!isBalanceEmpty && (
        <div className={stack()}>
          {allocations.map((allocation, i) => (
            <div key={i} className={hstack({ h: 28, justify: 'space-between' })}>
              <span className={hstack({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                {allocation.sumOfAppliedApr.multipliedBy(100).toString()}% APR
              </span>
              <Formatted.Number
                value={allocation.allocatedBalance}
                precision={NonUsdCurrencyList.includes(currency) ? NonUsdPrecision : UsdPrecision}
                suffix={UsdtCurrencyList.includes(currency) ? UsdtCurrency : currency}
                toFixed
                css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-4' }}
              />
            </div>
          ))}
          {noAprAppliedBalance && <NoAprApplied value={noAprAppliedBalance} currency={currency} />}
        </div>
      )}
    </details>
  );
};

const NoAprApplied = ({ value, currency }: { value: BigNumber; currency: string }) => {
  return (
    <div className={hstack({ h: 28, justify: 'space-between' })}>
      <span className={hstack({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>No APR applied</span>
      <Formatted.Number
        value={value}
        precision={NonUsdCurrencyList.includes(currency) ? NonUsdPrecision : UsdPrecision}
        suffix={UsdtCurrencyList.includes(currency) ? UsdtCurrency : currency}
        toFixed
        css={{ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-4' }}
      />
    </div>
  );
};
